import React, { useContext, useEffect, useReducer, useState } from "react";
import classes from "./Home.module.scss";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/Headers/PageHeader";
import PageMain from "../../../components/Main/PageMain";
import Spinner from "../../../components/Spinner";
import TabSecondEvaluation from "./TabSecondEvaluation";
import TabUserReports from "./TabUserReports";
import TabSmartTraining from "./TabSmartTraining";
import UserTrainingContext from "../../../context/userTrainingContext";
import WelcomeFirstAval from "../Tutorials/Welcome";
import useFetch from "../../../hooks/useFetch";
import { API_USER_SMART } from "../../../constants/api";
import SessionContext, { SessionInterface } from "../../../context/sessionContext";
import RoundButton from "../../../components/Buttons/RoundButton";
import AlertDialog from "../../../components/AlertDialog";
import moment from "moment";
import { GET_PROD_OR_DEV } from "../../../constants/api";
import { DEV } from "../../../constants/defaults";

// Constants
const isDev = String(GET_PROD_OR_DEV) === String(DEV);

// Action Types
const STAGE_ACTIONS = {
  SET_STAGE_1_FIRST_EVALUATION: "SET_STAGE_1_FIRST_EVALUATION",
  SET_STAGE_2_SECOND_TRAINING: "SET_STAGE_2_SECOND_TRAINING",
  SET_STAGE_3_SMART_TRAINING: "SET_STAGE_3_SMART_TRAINING",
  SET_STAGE_4_SMART_REPORTS: "SET_STAGE_4_SMART_REPORTS",
  SET_STAGE_5_SMART_END_GAMES_LISTS: "SET_STAGE_5_SMART_END_GAMES_LISTS",
  SET_STAGE_6_SMART_NO_SHOTS: "SET_STAGE_6_SMART_NO_SHOTS",
  SET_STAGE_7_SMART_END_JOURNEY: "SET_STAGE_7_SMART_END_JOURNEY",
} as const;

// Stage Mappings
const STAGE_NUMBERS = {
  FIRST_EVALUATION: 1,
  SMART_TRAINING: 2,
  SECOND_TRAINING: 3,
  SMART_REPORTS: 4,
  NO_SHOTS: 6,
  END_JOURNEY: 7,
  SPINNER: 8,
} as const;

const JOURNEY_CODE_TO_STAGE_MAP: { [key: string]: number } = {
  "-1": STAGE_NUMBERS.FIRST_EVALUATION,
  "0": STAGE_NUMBERS.FIRST_EVALUATION,
  "1": STAGE_NUMBERS.SMART_TRAINING,
  "2": STAGE_NUMBERS.SECOND_TRAINING,
  "3": STAGE_NUMBERS.SMART_REPORTS,
  "4": STAGE_NUMBERS.SMART_TRAINING,
};

// Interfaces
interface InitialStateInterface {
  stage: number;
  loading: boolean;
}

interface JourneyInfo {
  [key: string]: number;
}

// Data
const stagesData: { [key: number]: { title: string; subtitle: string } } = {
  1: { title: "global.stage_1_title", subtitle: "global.stage_1_subtitle" },
  2: { title: "global.training", subtitle: "global.stage_2_subtitle_training" },
  3: { title: "global.stage_3_title", subtitle: "global.stage_3_subtitle_reavaliation" },
  4: { title: "global.training", subtitle: "global.stage_end_journey" },
  5: { title: "global.stage_5_title", subtitle: "global.stage_5_subtitle" },
  6: { title: "global.no_shots", subtitle: "" },
  7: { title: "global.stage_1_title", subtitle: "" },
};

// Reducer
const stageReducer = (state: InitialStateInterface, action: { type: string }): InitialStateInterface => {
  const stageActionMap: { [key: string]: number } = {
    [STAGE_ACTIONS.SET_STAGE_1_FIRST_EVALUATION]: STAGE_NUMBERS.FIRST_EVALUATION,
    [STAGE_ACTIONS.SET_STAGE_3_SMART_TRAINING]: STAGE_NUMBERS.SMART_TRAINING,
    [STAGE_ACTIONS.SET_STAGE_2_SECOND_TRAINING]: STAGE_NUMBERS.SECOND_TRAINING,
    [STAGE_ACTIONS.SET_STAGE_4_SMART_REPORTS]: STAGE_NUMBERS.SMART_REPORTS,
    [STAGE_ACTIONS.SET_STAGE_5_SMART_END_GAMES_LISTS]: STAGE_NUMBERS.SMART_TRAINING,
    [STAGE_ACTIONS.SET_STAGE_6_SMART_NO_SHOTS]: STAGE_NUMBERS.NO_SHOTS,
    [STAGE_ACTIONS.SET_STAGE_7_SMART_END_JOURNEY]: STAGE_NUMBERS.END_JOURNEY,
  };

  const newStage = stageActionMap[action.type] ?? STAGE_NUMBERS.END_JOURNEY;

  return {
    ...state,
    stage: newStage,
  };
};

// Custom Hook
function useLocalStorage(key: string, initialValue: any): [any, (value: any) => void] {
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  const setValue = (value: any): void => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.error(error);
    }
  };

  return [storedValue, setValue];
}

// Initialize localStorage
localStorage.setItem("hasOpenGame", "False");

// Main Component
export default function Home() {
  // Hooks
  const { t } = useTranslation();
  const tContext = useContext(UserTrainingContext);
  const cSession: SessionInterface = useContext(SessionContext);

  // State
  const initialState: InitialStateInterface = {
    stage: STAGE_NUMBERS.SPINNER,
    loading: false,
  };
  const [state, dispatch] = useReducer(stageReducer, initialState);
  const [showReports, setShowReports] = useState(false);
  const [btnReportGames, setBtnReportGames] = useState("Relatórios");
  const [journeysInfo, setJourneysInfo] = useState<JourneyInfo>({});
  const [choosedShot, setedChoosedShot] = useState<string>("1");
  const [_contentBottom, setContentBottom] = useState<React.ReactNode>(<Spinner center />);
  const [newAlert, checkAlert] = useLocalStorage("newAlert", { hideAlert: false });

  // Fetch Hook
  const [userInfo, , isLoadingUserInfo, getSmartUserFetch]: any = useFetch({
    autoInit: false,
  });

  const { user_shots, game_list } = userInfo ?? {};

  // Helper Functions
  const getUser = async (c: any): Promise<any> => {
    const { user } = await c;
    return user;
  };

  const getUserSmartInfo = async (): Promise<void> => {
    try {
      const user = await getUser(cSession);
      if (user && user.token) {
        getSmartUserFetch({
          method: "GET",
          url: API_USER_SMART,
          noAuth: true,
          header: { userauthorization: user.token },
          onSuccess: () => { },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const setFetchedJourneys = (f: any): void => {
    const j: JourneyInfo = f
      ? {
        numberOfJourneys: Object.keys(f).length,
        showSelect: 1,
      }
      : {};
    setJourneysInfo(j);
  };

  const setJourney = (j: string | number): void => {
    setedChoosedShot(String(j));
    dispatch({ type: STAGE_ACTIONS.SET_STAGE_7_SMART_END_JOURNEY });
  };

  const goToReports = (): void => {
    if (!showReports) {
      setBtnReportGames("Jogos");
      setShowReports(!showReports);
      dispatch({ type: STAGE_ACTIONS.SET_STAGE_4_SMART_REPORTS });
    } else {
      setBtnReportGames("Relatórios");
      setShowReports(!showReports);
      dispatch({ type: STAGE_ACTIONS.SET_STAGE_3_SMART_TRAINING });
    }
  };

  const calculateNewAlertDate = (dateIAFinish: Date | undefined): Date => {
    let dateIAFinishISO: Date = new Date();
    if (dateIAFinish) {
      dateIAFinishISO = new Date(dateIAFinish);
    }

    const baseDate = moment(dateIAFinishISO);
    const daysToAdd = isDev ? -2 : -30;

    return baseDate
      .add(daysToAdd, "d")
      .add(1, "m")
      .add(-3, "h")
      .toDate();
  };

  const shouldShowNewAlert = (): boolean => {
    return (
      !newAlert.hideAlert ||
      newAlert.userId != userInfo?.user_uid ||
      newAlert.journey != userInfo?.journey ||
      newAlert.journey_code != userInfo?.journey_code
    ) && !(userInfo?.journey_code === 3);
  };

  const getContentForStage = (stage: number): React.ReactNode => {
    const stageComponents: { [key: number]: React.ReactNode } = {
      1: <WelcomeFirstAval />,
      2: <TabSmartTraining game_list={tContext?.game_list} />,
      3: <TabSecondEvaluation />,
      4: <TabUserReports
        setFetchedJourneys={setFetchedJourneys}
        setChoosedShot={setJourney}
        choosedShot={choosedShot}
      />,
      5: <TabUserReports
        setFetchedJourneys={setFetchedJourneys}
        setChoosedShot={setJourney}
        choosedShot={choosedShot}
      />,
      6: <>Sem jornadas</>,
      7: <TabUserReports
        setFetchedJourneys={setFetchedJourneys}
        setChoosedShot={setJourney}
        choosedShot={choosedShot}
      />,
      8: <Spinner center />,
    };
    return stageComponents[stage] || <Spinner center />;
  };

  // Effects
  useEffect(() => {
    if (localStorage.getItem("hasOpenGame") === "True") {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    getUserSmartInfo();
  }, [cSession]);

  useEffect(() => {
    setContentBottom(getContentForStage(state.stage));
  }, [state.stage, choosedShot]);

  useEffect(() => {
    if (userInfo) {
      if (user_shots >= 1 || userInfo?.journey_code !== 5) {
        state.stage = JOURNEY_CODE_TO_STAGE_MAP[String(userInfo?.journey_code)];
      } else if (user_shots !== undefined && user_shots === 0) {
        state.stage = STAGE_NUMBERS.NO_SHOTS;
      }
    }
  }, [userInfo, isLoadingUserInfo, getSmartUserFetch]);

  // CORREÇÃO IMPORTANTE: Adicione a verificação de showReports aqui
  useEffect(() => {
    // NÃO executa se está mostrando relatórios
    if (showReports) return;
    
    if (userInfo && userInfo?.journey_code === 1) {
      dispatch({ type: STAGE_ACTIONS.SET_STAGE_3_SMART_TRAINING });
    } else if (userInfo && userInfo?.journey_code === 4) {
      dispatch({ type: STAGE_ACTIONS.SET_STAGE_5_SMART_END_GAMES_LISTS });
    } else if (
      userInfo &&
      userInfo?.journey_code === 3 &&
      userInfo?.user_shots === 1
    ) {
      dispatch({ type: STAGE_ACTIONS.SET_STAGE_1_FIRST_EVALUATION });
    }
  }, [userInfo, showReports]); // Adicione showReports nas dependências

  useEffect(() => {
    if (
      user_shots !== undefined &&
      user_shots === 0 &&
      userInfo?.journey_code === 3
    ) {
      state.stage = STAGE_NUMBERS.END_JOURNEY;
      dispatch({ type: STAGE_ACTIONS.SET_STAGE_7_SMART_END_JOURNEY });
    }
  }, [userInfo, isLoadingUserInfo, getSmartUserFetch]);

  // Effect para verificar se deve mostrar relatórios ao carregar
  useEffect(() => {
    // Verifica se tem a flag no sessionStorage
    const shouldShowReports = sessionStorage.getItem('showReports') === 'true';

    if (shouldShowReports && state.stage === STAGE_NUMBERS.SMART_TRAINING) {
      // Remove a flag do sessionStorage
      sessionStorage.removeItem('showReports');

      // Ativa os relatórios
      setBtnReportGames("Jogos");
      setShowReports(true);
      dispatch({ type: STAGE_ACTIONS.SET_STAGE_4_SMART_REPORTS });
    }
  }, [state.stage]);

  // Computed Values
  const _pageSubTitle =
  stagesData[state.stage]?.subtitle
    ? t(stagesData[state.stage].subtitle)
    : "";
  const newDateAlert = calculateNewAlertDate(userInfo?.date_IA_finish);
  const showButtonForStages: number[] = [
    STAGE_NUMBERS.SMART_TRAINING,
    STAGE_NUMBERS.FIRST_EVALUATION,
    STAGE_NUMBERS.SMART_REPORTS
  ];

  // Render
  console.log("STAGE_NUMBERS.SMART_TRAINING", STAGE_NUMBERS.SMART_TRAINING);
  return (
    <>
      <PageHeader
        // title={t("global.welcome") + ", " + String(tContext.registry)}
        subtitle={_pageSubTitle}
        chooseJourney={journeysInfo}
        setJourney={setJourney}
      />

      {state.stage === STAGE_NUMBERS.SMART_TRAINING && showReports && _contentBottom}

      <PageMain centerStretch>
        <div>
          <div className={classes.PageHome}>
            <section className={classes.ContentArea}>
              <div>
                {!(state.stage === STAGE_NUMBERS.SMART_TRAINING && showReports) && _contentBottom}
              </div>
            </section>
          </div>
        </div>
      </PageMain>
    </>
  );
}